import { useState, useRef, useCallback } from 'react';
import { ScanLine, Upload, Copy, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { useToolUsage } from '@/hooks/useToolUsage';

const QrReader = () => {
  useToolUsage('qr-reader');
  const [result, setResult] = useState('');
  const [preview, setPreview] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) { toast.error('Upload an image'); return; }
    setPreview(URL.createObjectURL(file));

    // Use BarcodeDetector API if available
    if ('BarcodeDetector' in window) {
      try {
        const img = new Image();
        img.src = URL.createObjectURL(file);
        await new Promise(r => { img.onload = r; });
        const detector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });
        const codes = await detector.detect(img);
        if (codes.length > 0) {
          setResult(codes[0].rawValue);
          toast.success('QR Code decoded!');
        } else {
          setResult('');
          toast.error('No QR code found in image');
        }
      } catch {
        toast.error('Failed to decode QR code');
      }
    } else {
      toast.error('QR scanning not supported in this browser. Try Chrome or Edge.');
    }
  }, []);

  const copy = () => {
    navigator.clipboard.writeText(result);
    toast.success('Copied!');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <ScanLine className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">QR Code Reader</h1>
          <p className="text-muted-foreground">Upload an image to decode QR codes</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <label className="block cursor-pointer">
            <div className="border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-primary transition-colors">
              <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="font-medium">Upload image with QR code</p>
              <p className="text-sm text-muted-foreground mt-1">PNG, JPG supported</p>
            </div>
            <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
          </label>

          {preview && (
            <div className="flex justify-center">
              <img src={preview} alt="QR" className="max-h-48 rounded-lg border border-border" />
            </div>
          )}

          {result && (
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Decoded content:</p>
              <p className="font-mono text-sm break-all">{result}</p>
              <Button size="sm" variant="outline" className="mt-2" onClick={copy}>
                <Copy className="h-3 w-3 mr-1" /> Copy
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default QrReader;
